import React, { useState, useEffect } from 'react';
import { Modal } from '../../../Components/Modal';
import { Button } from '../../../Components/Button';
import { Input } from '../../../Components/Input';
import { Title, Text } from '../../../Components/Typography';
import { PrecioAnillado } from '../../../hooks/usePreciosAnillado';
import './BulkEditAnilladoModal.scss';

interface BulkEditAnilladoModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedPrecios: PrecioAnillado[];
  onSave: (updates: { id: number; newPrice: number }[]) => Promise<void>;
  loading: boolean;
}

type EditMode = 'percentage' | 'fixed' | 'custom';

export const BulkEditAnilladoModal: React.FC<BulkEditAnilladoModalProps> = ({
  isOpen,
  onClose,
  selectedPrecios,
  onSave,
  loading
}) => {
  const [editMode, setEditMode] = useState<EditMode>('percentage');
  const [percentageValue, setPercentageValue] = useState<string>('');
  const [fixedValue, setFixedValue] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [previewUpdates, setPreviewUpdates] = useState<{ 
    id: number; 
    currentPrice: number; 
    newPrice: number;
    isProblematic?: boolean;
  }[]>([]);

  useEffect(() => {
    if (isOpen) {
      // Limpiar completamente todos los estados al abrir el modal
      setPercentageValue('');
      setFixedValue('');
      setError('');
      setPreviewUpdates([]);
    }
  }, [isOpen]);

  const calculateNewPrices = () => {
    if (!selectedPrecios.length) return [];

    const updates = selectedPrecios.map(precio => {
      let newPrice = precio.precio;

      switch (editMode) {
        case 'percentage':
          const percentage = parseFloat(percentageValue);
          if (!isNaN(percentage)) {
            newPrice = precio.precio * (1 + percentage / 100);
          }
          break;
        case 'fixed':
          const fixed = parseFloat(fixedValue);
          if (!isNaN(fixed)) {
            newPrice = precio.precio + fixed;
          }
          break;
        case 'custom':
          // Para modo personalizado, cada precio se edita individualmente
          newPrice = precio.precio;
          break;
      }

      const finalPrice = Math.round(newPrice * 100) / 100;
      const isProblematic = finalPrice <= 0;

      return {
        id: precio.id,
        currentPrice: precio.precio,
        newPrice: finalPrice,
        isProblematic
      };
    });

    setPreviewUpdates(updates);
    return updates;
  };

  useEffect(() => {
    if (isOpen) {
      if (percentageValue || fixedValue) {
        calculateNewPrices();
      } else {
        // Si no hay valores, limpiar la vista previa
        setPreviewUpdates([]);
      }
    }
  }, [editMode, percentageValue, fixedValue, selectedPrecios, isOpen]);

  const handleSave = async () => {
    if (selectedPrecios.length === 0) return;

    const updates = calculateNewPrices();
    
    if (updates.length === 0) {
      setError('No hay cambios para aplicar');
      return;
    }

    // Filtrar solo los precios que no son problemáticos (se excluyen automáticamente)
    const validUpdates = updates.filter(update => !update.isProblematic);

    if (validUpdates.length === 0) {
      setError('No hay precios válidos para aplicar');
      return;
    }

    try {
      await onSave(validUpdates);
      // Limpiar todos los estados antes de cerrar
      setPercentageValue('');
      setFixedValue('');
      setError('');
      setPreviewUpdates([]);
      onClose();
    } catch (error) {
      setError('Error al guardar los cambios');
    }
  };

  const formatPrice = (price: number) => {
    return `$${price.toLocaleString('es-AR')}`;
  };

  const formatRange = (min: number, max: number) => {
    return `${min}-${max} hojas`;
  };

  const getValidUpdatesCount = () => {
    return previewUpdates.filter(update => !update.isProblematic).length;
  };

  const getProblematicUpdates = () => {
    return previewUpdates.filter(update => update.isProblematic);
  };

  return (
    <Modal
      title={`Edición Masiva - ${selectedPrecios.length} precios seleccionados`}
      open={isOpen}
      onClose={onClose}
    >
      <div className="bulk-edit-modal">
        {/* Modo de edición */}
        <div className="edit-mode-section">
          <Text bold>Modo de Edición:</Text>
          <div className="mode-buttons">
            <Button
              onClick={() => {
                setEditMode('percentage');
                setFixedValue(''); // Limpiar campo de monto fijo
                setError(''); // Limpiar errores
                setPreviewUpdates([]); // Limpiar vista previa
              }}
              outlined={editMode !== 'percentage'}
              small
              style={editMode === 'percentage' ? { backgroundColor: '#FE7C40', color: 'white' } : {}}
            >
              📊 Porcentaje
            </Button>
            <Button
              onClick={() => {
                setEditMode('fixed');
                setPercentageValue(''); // Limpiar campo de porcentaje
                setError(''); // Limpiar errores
                setPreviewUpdates([]); // Limpiar vista previa
              }}
              outlined={editMode !== 'fixed'}
              small
              style={editMode === 'fixed' ? { backgroundColor: '#FE7C40', color: 'white' } : {}}
            >
              💰 Monto Fijo
            </Button>
          </div>
        </div>

        {/* Input según el modo */}
        <div className="input-section">
          {editMode === 'percentage' && (
            <div className="input-group">
              <Input
                title="Porcentaje de cambio"
                placeholder="Ej: 10 para aumentar 10%, -5 para disminuir 5%"
                type="number"
                value={percentageValue}
                onChange={setPercentageValue}
                error={error}
              />
              <Text className="input-help">
                Ingrese un número positivo para aumentar, negativo para disminuir
              </Text>
            </div>
          )}

          {editMode === 'fixed' && (
            <div className="input-group">
              <Input
                title="Monto fijo a agregar/quitar"
                placeholder="Ej: 5 para agregar $5, -2 para quitar $2"
                type="number"
                value={fixedValue}
                onChange={setFixedValue}
                error={error}
              />
              <Text className="input-help">
                Ingrese un número positivo para agregar, negativo para quitar
              </Text>
            </div>
          )}
        </div>

          {previewUpdates.length > 0 && (
            <div className="preview-section">
              <div className="preview-header">
                <Text bold>Vista Previa de Cambios</Text>
                <div className="preview-stats">
                  <div className="stat">
                    <Text>Válidos: {getValidUpdatesCount()}</Text>
                  </div>
                </div>
              </div>

              {/* Mostrar precios problemáticos primero */}
              {getProblematicUpdates().length > 0 && (
                <div className="problematic-section">
                  <div className="problematic-header">
                    <div className="warning-text">
                      ⚠️ {getProblematicUpdates().length} productos serán excluidos automáticamente. Sus precios se convertirían en valores negativos o cero
                    </div>
                  </div>
                  <div className="problematic-list">
                    {getProblematicUpdates().map((update) => {
                      const precio = selectedPrecios.find(p => p.id === update.id);
                      if (!precio) return null;

                      return (
                        <div key={update.id} className="preview-item problematic">
                          <div className="precio-info">
                            <Text bold>{precio.hoja_name}</Text>
                            <Text>{formatRange(precio.min_hojas, precio.max_hojas)}</Text>
                          </div>
                          <div className="price-change">
                            <Text className="old-price">
                              {formatPrice(update.currentPrice)}
                            </Text>
                            <Text>→</Text>
                            <Text bold className="new-price problematic">
                              {formatPrice(update.newPrice)}
                            </Text>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Mostrar precios válidos */}
              <div className="valid-prices-section">
                <Text bold>Precios Válidos</Text>
                <div className="preview-list">
                  {previewUpdates
                    .filter(update => !update.isProblematic)
                    .map((update) => {
                      const precio = selectedPrecios.find(p => p.id === update.id);
                      if (!precio) return null;

                      return (
                        <div key={update.id} className="preview-item">
                          <div className="precio-info">
                            <Text bold>{precio.hoja_name}</Text>
                            <Text>{formatRange(precio.min_hojas, precio.max_hojas)}</Text>
                          </div>
                          <div className="price-change">
                            <Text className="old-price">
                              {formatPrice(update.currentPrice)}
                            </Text>
                            <Text>→</Text>
                            <Text bold className="new-price">
                              {formatPrice(update.newPrice)}
                            </Text>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          )}

        {/* Acciones */}
        <div className="modal-actions">
          <Button 
            onClick={onClose}
            outlined
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSave}
            disabled={loading || getValidUpdatesCount() === 0}
            loading={loading}
          >
            {`Aplicar Cambios (${getValidUpdatesCount()} precios)`}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
